import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Star, ShoppingCart, Heart, Truck, Shield, RefreshCw, Minus, Plus, Check } from 'lucide-react';
import { getProductBySlug, getRelatedProducts } from '../data/products';
import { useCart } from '../context/CartContext';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);

  const product = getProductBySlug(slug);
  const relatedProducts = product ? getRelatedProducts(product.id) : [];

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="product-not-found">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-gray-900 mb-4">Product not found</h1>
          <Button onClick={() => navigate('/')} className="btn-primary">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleBuyNow = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    navigate('/checkout');
  };

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;

  const reviews = [
    { id: 1, name: 'Jennifer L.', rating: 5, date: '2 weeks ago', text: 'Excellent quality! My dog loves it and it has lasted longer than any other toy we\'ve bought.' },
    { id: 2, name: 'Michael S.', rating: 5, date: '1 month ago', text: 'Great toy, exactly as described. Fast shipping too!' },
    { id: 3, name: 'Sarah K.', rating: 4, date: '3 weeks ago', text: 'Good toy, my cat plays with it daily. Would recommend.' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="product-page">
      {/* Breadcrumb */}
      <div className="bg-[#FFF9F5] border-b">
        <div className="container-main py-4">
          <nav className="flex items-center text-sm text-gray-600 flex-wrap">
            <Link to="/" className="hover:text-brand-orange" data-testid="breadcrumb-home">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to={`/category/${product.category}`} className="hover:text-brand-orange" data-testid="breadcrumb-category">
              {product.category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="container-main section-padding">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4" data-testid="product-images">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="aspect-square rounded-3xl overflow-hidden bg-gray-100"
            >
              <img
                src={product.images[selectedImage] || product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </motion.div>
            {product.images.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? 'border-brand-orange' : 'border-transparent'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="lg:sticky lg:top-24 lg:self-start space-y-6" data-testid="product-info">
            {/* Badges */}
            <div className="flex gap-2">
              {discount && (
                <Badge className="bg-brand-orange text-white">{discount}% OFF</Badge>
              )}
              {product.isBestSeller && (
                <Badge className="bg-brand-green text-white">Best Seller</Badge>
              )}
              {product.isNewArrival && (
                <Badge className="bg-brand-blue text-white">New Arrival</Badge>
              )}
            </div>

            {/* Title & Rating */}
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {product.name}
              </h1>
              <div className="flex items-center gap-3">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.floor(product.rating)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-gray-600">{product.rating} ({product.reviewCount} reviews)</span>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <span className="font-heading text-4xl font-bold text-brand-orange">
                ${product.price.toFixed(2)}
              </span>
              {product.originalPrice && (
                <span className="text-xl text-gray-400 line-through">
                  ${product.originalPrice.toFixed(2)}
                </span>
              )}
            </div>

            {/* Description */}
            <p className="text-gray-600 text-lg">{product.description}</p>

            {/* Features */}
            <div className="space-y-2">
              {product.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-gray-700">
                  <Check className="w-5 h-5 text-brand-green" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            {/* Quantity & Add to Cart */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-4">
                <span className="text-gray-700 font-medium">Quantity:</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-2 rounded-full border hover:bg-gray-100 transition-colors"
                    data-testid="decrease-qty"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-12 text-center font-medium text-lg">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-2 rounded-full border hover:bg-gray-100 transition-colors"
                    data-testid="increase-qty"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={handleAddToCart}
                  className={`flex-1 btn-primary ${addedToCart ? 'bg-brand-green hover:bg-brand-green-hover' : ''}`}
                  data-testid="add-to-cart-btn"
                >
                  {addedToCart ? (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Added to Cart!
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      Add to Cart
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleBuyNow}
                  className="flex-1 bg-gray-900 text-white hover:bg-gray-800 rounded-full"
                  data-testid="buy-now-btn"
                >
                  Buy Now
                </Button>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t">
              <div className="text-center">
                <Truck className="w-6 h-6 text-brand-orange mx-auto mb-2" />
                <span className="text-sm text-gray-600">Free Shipping $50+</span>
              </div>
              <div className="text-center">
                <Shield className="w-6 h-6 text-brand-orange mx-auto mb-2" />
                <span className="text-sm text-gray-600">Secure Checkout</span>
              </div>
              <div className="text-center">
                <RefreshCw className="w-6 h-6 text-brand-orange mx-auto mb-2" />
                <span className="text-sm text-gray-600">30-Day Returns</span>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="mt-16" data-testid="product-tabs">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="w-full justify-start bg-transparent border-b rounded-none p-0 h-auto">
              <TabsTrigger
                value="details"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-orange data-[state=active]:bg-transparent px-6 py-3"
              >
                Product Details
              </TabsTrigger>
              <TabsTrigger
                value="shipping"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-orange data-[state=active]:bg-transparent px-6 py-3"
              >
                Shipping & Returns
              </TabsTrigger>
              <TabsTrigger
                value="reviews"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-orange data-[state=active]:bg-transparent px-6 py-3"
              >
                Reviews ({product.reviewCount})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="pt-6">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-heading font-bold text-lg mb-3">Specifications</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Material</span>
                      <span className="font-medium">{product.material}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Recommended For</span>
                      <span className="font-medium">{product.recommendedSize}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Pet Type</span>
                      <span className="font-medium">{product.petType}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Toy Type</span>
                      <span className="font-medium">{product.toyType}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg mb-3">Features</h3>
                  <ul className="space-y-2">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="shipping" className="pt-6">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-heading font-bold text-lg mb-3">Shipping</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <Truck className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
                      <span>Free standard shipping on orders over $50</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Truck className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
                      <span>Standard shipping (3-7 business days): $5.99</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Truck className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
                      <span>Express shipping (1-3 business days): $12.99</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg mb-3">Returns</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <RefreshCw className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
                      <span>30-day hassle-free returns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <RefreshCw className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
                      <span>Items must be unused and in original packaging</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <RefreshCw className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
                      <span>Full refund or exchange available</span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="reviews" className="pt-6">
              <div className="space-y-6">
                {reviews.map(review => (
                  <div key={review.id} className="bg-[#FFF9F5] p-6 rounded-2xl">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-brand-orange text-white rounded-full flex items-center justify-center font-bold">
                          {review.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{review.name}</p>
                          <p className="text-sm text-gray-500">{review.date}</p>
                        </div>
                      </div>
                      <div className="flex">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-700">{review.text}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16" data-testid="related-products">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Add to Cart - Mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-40" data-testid="sticky-cart">
        <div className="flex items-center gap-4">
          <div>
            <p className="font-heading font-bold text-xl text-brand-orange">${product.price.toFixed(2)}</p>
          </div>
          <Button onClick={handleAddToCart} className="flex-1 btn-primary" data-testid="sticky-add-to-cart">
            <ShoppingCart className="w-5 h-5 mr-2" />
            Add to Cart
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
